import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { Box, Palette, PaletteColor, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    ObjectGroupSelector,
    TiersObjectGroup,
    useTiersService,
} from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/sharp-solid-svg-icons';
import { AnyAction } from '@reduxjs/toolkit';
import { useSocietesObjectGroup } from '@europrocurement/l2d-modules/modules/Societes/hooks';
import { ActionMenuListSocieteGroup } from '../ActionMenuListSocieteGroup';

export const SocieteGroupTable = function () {
    const dispatch = useDispatch();

    const datasource: DataSource<TiersObjectGroup> = useSelector(ObjectGroupSelector).main;
    const { getObjectGroup } = useTiersService();

    // filter
    const { searchTiersObjectGroup } = useSocietesObjectGroup();
    const { palette } = useTheme();

    const filters: FiltersDatatableList = [
        {
            label: 'Groupes',
            field: 'label',
            type: 'selectApi',
            icon: 'buildings',
            searchFunction: searchTiersObjectGroup,
            renderItem: (item: TiersObjectGroup) => item.label as string,
            renderIcon: (item: TiersObjectGroup, defaultIcon) => {
                const icon = item?.icon || defaultIcon;
                const color = item?.color;

                return (
                    <FaOptionIcon
                        sx={{
                            fontSize: '12px',
                            lineHeight: '0',
                            color: color ? `${color}.main` : 'inherit',
                        }}
                        icon={['fasl', icon as IconName]}
                    />
                );
            },
            valueMap: (item: TiersObjectGroup) => item.label as string,
        },
    ];

    const columns: ColumnDatatable<TiersObjectGroup>[] = [
        {
            label: 'Groupe',
            render: ({ label, color = 'primary' }) => {
                const iconColor = color && color !== '' ? color : 'primary';
                const paletteColor = palette[iconColor as keyof Palette] as PaletteColor;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FaOptionIcon
                            icon={['fasl', 'buildings']}
                            color={paletteColor.main}
                        />
                        <Typography>{label}</Typography>
                    </Box>
                );
            },
            displayLabel: false,
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
        {
            label: 'Nombre de sociétés',
            render: ({ childsCount }) => (
                <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%', gap: '5px' }}>
                    <Typography>
                        {`${childsCount} ${childsCount && childsCount > 1 ? ' sociétés' : 'société'}`}
                    </Typography>
                </Box>
            ),
            isDisplayed: true,
            sortable: false,
            displayLabel: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
                textAlign: 'end',
            },
        },
        {
            label: 'Actions',
            displayLabel: false,
            render: (societeGroupe: TiersObjectGroup) => (
                <ActionMenuListSocieteGroup societeGroupe={societeGroupe} />
            ),
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'flex',
                    lg: 'flex',
                    xl: 'flex',
                },
                justifyContent: 'end',
            },
        },
    ];

    useEffect(() => {
        dispatch(getObjectGroup({}) as unknown as AnyAction);
    }, [getObjectGroup, dispatch]);

    return (
        <StoreDatatable
            dataSource={datasource}
            columns={columns}
            hideColumnOptions
            fetchData={getObjectGroup}
            localStorageKey="FlexyStoreDatatable"
            filters={filters}
        />
    );
};
